const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const ACTIVITY_FACTORS = {
  low: 1.2,
  light: 1.375,
  moderate: 1.55,
  high: 1.725,
  veryHigh: 1.9,
};

const kgFrom = (value, unit = "lb") => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  return unit === "kg" ? number : number * 0.45359237;
};

const kgTo = (value, unit = "lb") =>
  unit === "kg" ? value : value / 0.45359237;

const cmFrom = (value, unit = "cm") => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  return unit === "in" ? number * 2.54 : number;
};

const mifflin = ({ kg, cm, age, offset }) =>
  10 * kg + 6.25 * cm - 5 * age + offset;

const macroCalories = ({ protein = 0, carbs = 0, fat = 0 } = {}) =>
  Math.round(Number(protein || 0) * 4 + Number(carbs || 0) * 4 + Number(fat || 0) * 9);

export function estimateEnergyNeeds({ weight, weightUnit, height, heightUnit, age, sex, activity }) {
  const kg = kgFrom(weight, weightUnit);
  const cm = cmFrom(height, heightUnit);
  const years = Number(age);
  if (!kg || !cm || !Number.isFinite(years) || years < 18) return null;

  const female = mifflin({ kg, cm, age: years, offset: -161 });
  const male = mifflin({ kg, cm, age: years, offset: 5 });
  const factor = ACTIVITY_FACTORS[activity] || ACTIVITY_FACTORS.light;
  const rmr = sex === "female" ? female : sex === "male" ? male : (female + male) / 2;
  const maintenance = rmr * factor;

  return {
    method: "Mifflin-St Jeor working estimate",
    rmr: Math.round(rmr),
    maintenance: Math.round(maintenance),
    maintenanceRange:
      sex === "female" || sex === "male"
        ? null
        : [Math.round(Math.min(female, male) * factor), Math.round(Math.max(female, male) * factor)],
    kg,
    cm,
  };
}

export function evaluateBodyMassGoal(profile = {}, identity = {}) {
  if (!profile.enabled) return { enabled: false };
  const energy = estimateEnergyNeeds({
    ...profile,
    age: identity.age,
    sex: identity.sex,
  });
  if (!energy) {
    return {
      enabled: true,
      ready: false,
      message: "Add current body mass and height before calculating a working energy estimate.",
    };
  }

  const currentKg = energy.kg;
  const targetKg = kgFrom(profile.targetWeight, profile.weightUnit);
  const heightM = energy.cm / 100;
  const currentBmi = currentKg / (heightM * heightM);
  const targetBmi = targetKg ? targetKg / (heightM * heightM) : null;
  const direction = profile.direction || "maintain";
  const today = new Date();
  const targetDate = profile.targetDate ? new Date(`${profile.targetDate}T12:00:00`) : null;
  const days = targetDate && !Number.isNaN(targetDate.getTime())
    ? Math.max(1, Math.ceil((targetDate.getTime() - today.getTime()) / 86400000))
    : null;

  const errors = [];
  if (direction === "reduce" && currentBmi <= 18.5) {
    errors.push("The automatic reduction calculator is paused at this current body-mass range.");
  }
  if (direction === "reduce" && targetBmi != null && targetBmi < 18.5) {
    errors.push("That reduction target falls below the calculator's adult screening boundary and will not be converted into a calorie or macro target.");
  }
  if (targetKg && direction === "reduce" && targetKg >= currentKg) {
    errors.push("The target needs to be below current body mass for a reduction goal.");
  }
  if (targetKg && direction === "gain" && targetKg <= currentKg) {
    errors.push("The target needs to be above current body mass for a gain goal.");
  }

  const maxStageChange = currentKg * 0.1;
  const requestedChangeKg = targetKg ? targetKg - currentKg : 0;
  const staged = Math.abs(requestedChangeKg) > maxStageChange;
  const stagedTargetKg = targetKg
    ? currentKg + Math.sign(requestedChangeKg || 1) * Math.min(Math.abs(requestedChangeKg), maxStageChange)
    : currentKg;

  const requestedDailyBalance = targetKg && days
    ? (requestedChangeKg * 7700) / days
    : 0;
  const maxAutomaticChange = energy.maintenance * 0.2;
  const boundedDailyBalance = clamp(requestedDailyBalance, -maxAutomaticChange, maxAutomaticChange);
  let workingCalories = Math.round(energy.maintenance + boundedDailyBalance);
  let aggressive = Math.abs(requestedDailyBalance) > maxAutomaticChange;

  if (direction === "maintain") workingCalories = energy.maintenance;
  if (direction === "reduce" && workingCalories < 1200) {
    aggressive = true;
    workingCalories = Math.round(Math.max(1200, energy.maintenance * 0.8));
  }
  if (errors.length) workingCalories = null;

  const currentMacros = {
    protein: Number(profile.currentProtein) || 0,
    carbs: Number(profile.currentCarbs) || 0,
    fat: Number(profile.currentFat) || 0,
  };
  const currentMacroCalories = macroCalories(currentMacros);
  const macroBase = workingCalories || energy.maintenance;
  const suggestedMacros = {
    protein: Math.round((macroBase * 0.25) / 4),
    carbs: Math.round((macroBase * 0.45) / 4),
    fat: Math.round((macroBase * 0.3) / 9),
  };
  const selectedMacros = {
    protein: Number(profile.targetProtein) || suggestedMacros.protein,
    carbs: Number(profile.targetCarbs) || suggestedMacros.carbs,
    fat: Number(profile.targetFat) || suggestedMacros.fat,
  };
  const selectedMacroCalories = macroCalories(selectedMacros);

  const intakeReference = Number(profile.currentCalories) || currentMacroCalories || null;
  const energyGapPercent = intakeReference
    ? Math.round(((intakeReference - energy.maintenance) / energy.maintenance) * 100)
    : workingCalories
      ? Math.round(((workingCalories - energy.maintenance) / energy.maintenance) * 100)
      : 0;

  const adverseObserved =
    profile.energyEffect === "lower" ||
    profile.recoveryEffect === "worse" ||
    profile.irritabilityEffect === "higher";
  const nutritionLoadPenalty =
    energyGapPercent <= -15 && adverseObserved
      ? energyGapPercent <= -20 ? 18 : 10
      : 0;

  const nervousSystemNotes = [];
  if (energyGapPercent <= -15) {
    nervousSystemNotes.push(
      "A sustained intake meaningfully below estimated demand may reduce usable energy or recovery for some people, especially when training or life load is high.",
    );
  }
  if (profile.energyEffect === "lower") {
    nervousSystemNotes.push("You reported lower usable energy with the current eating pattern, so APOS treats that as a capacity signal rather than automatically adding more exercise.");
  } else if (profile.energyEffect === "higher") {
    nervousSystemNotes.push("You reported better usable energy. APOS records that response, but it does not automatically authorize a harder exercise dose.");
  }
  if (profile.irritabilityEffect === "higher") {
    nervousSystemNotes.push("You reported more irritability. APOS can use that alongside sleep, hunger, workload, and current state when interpreting the day rather than assuming one cause.");
  }
  if (profile.recoveryEffect === "worse") {
    nervousSystemNotes.push("You reported slower recovery, so the state-matching engine can reduce additional physical demand when the rest of the current context supports that adjustment.");
  }

  return {
    enabled: true,
    ready: errors.length === 0,
    errors,
    direction,
    currentWeight: Number(profile.weight) || null,
    targetWeight: Number(profile.targetWeight) || null,
    weightUnit: profile.weightUnit || "lb",
    currentBmi: Number(currentBmi.toFixed(1)),
    targetBmi: targetBmi == null ? null : Number(targetBmi.toFixed(1)),
    maintenanceCalories: energy.maintenance,
    maintenanceRange: energy.maintenanceRange,
    workingCalories,
    requestedDailyBalance: Math.round(requestedDailyBalance),
    aggressive,
    staged,
    stagedTargetWeight: Number(kgTo(stagedTargetKg, profile.weightUnit).toFixed(1)),
    currentMacroCalories,
    selectedMacroCalories,
    suggestedMacros,
    selectedMacros,
    energyGapPercent,
    nutritionLoadPenalty,
    adverseObserved,
    nervousSystemNotes,
    targetDate: profile.targetDate || null,
    requestedWeeklyChangeKg:
      targetKg && days ? Number(((requestedChangeKg / days) * 7).toFixed(2)) : null,
  };
}

export function adjustMacro(profile = {}, macro, grams) {
  const key = {
    protein: "targetProtein",
    carbs: "targetCarbs",
    fat: "targetFat",
  }[macro];
  if (!key) return profile;
  return {
    ...profile,
    [key]: Math.max(0, (Number(profile[key]) || 0) + Number(grams || 0)),
  };
}

export function nutritionStateMatchContext(profile = {}, identity = {}) {
  const result = evaluateBodyMassGoal(profile, identity);
  if (!result.enabled || !result.ready) return null;
  return {
    enabled: true,
    direction: result.direction,
    maintenanceCalories: result.maintenanceCalories,
    workingCalories: result.workingCalories,
    energyGapPercent: result.energyGapPercent,
    nutritionLoadPenalty: result.nutritionLoadPenalty,
    adverseObserved: result.adverseObserved,
    nervousSystemNotes: result.nervousSystemNotes,
  };
}
